import { mountCountdown, type Countdown } from '@/ui/countdown';
import { entryIsWaiting, entryLabel } from '@/ui/faucet-copy';
import { currentFaucet, faucetIsOff, loadFaucet, subscribeFaucet } from '@/ui/faucet-state';
import { el } from '@/ui/hud';

/**
 * The "Free tokens" button: on home (the account box) and on MY OWN profile's money card, never anywhere else
 * (Blockari's `Home` link and `shell/ProfileFaucet.tsx` @ 17aabed). Both are this one component, so the two always
 * say the same thing:
 *
 *   ready                "Free tokens · +100", the warm button
 *   cooling down         "Free tokens in 3d 4h", the quiet default button (not warm), still a link
 *   guest / no game yet / not loaded / the call failed   plain "Free tokens", the warm button
 *
 * It reads the shared faucet state (`faucet-state.ts`): one GET /api/faucet when it is given an account, a repaint
 * when a claim replaces the state. It is only ever built when the flag is on (`FAUCET_ENABLED`).
 */
export interface FaucetEntry {
  readonly root: HTMLAnchorElement;
  /** Whose button it is (null: nobody, hidden). A guest has no state to read, so no request is made for one. */
  setAccount(username: string | null, isGuest: boolean): void;
  destroy(): void;
}

export function faucetEntry(onOpen: () => void, now: () => number = Date.now): FaucetEntry {
  const root = el('a', 'w21-btn w21-sm w21-warm w21-faucet-entry') as HTMLAnchorElement;
  root.href = '#faucet';
  root.dataset.testid = 'faucet-entry';
  root.hidden = true;
  root.addEventListener('click', (event) => {
    event.preventDefault();
    onOpen();
  });
  let username: string | null = null;
  let guest = false;
  /** The ticking wait while the button is cooling down, and the claim time it counts to. */
  let ticking: { countdown: Countdown; to: number } | null = null;

  const stopTicking = (): void => {
    ticking?.countdown.destroy();
    ticking = null;
  };
  /** The wait ran out: read the faucet again. The button flips to ready (or the wait moves) through `paint`. */
  const elapsed = (): void => {
    if (username !== null) void loadFaucet(username, { force: true });
  };

  const paint = (): void => {
    // Hidden for nobody, and once the server has said the faucet is off (its flag, or an origin that is not public).
    root.hidden = username === null || faucetIsOff();
    if (root.hidden) {
      stopTicking();
      return;
    }
    const state = guest ? null : currentFaucet(username);
    // While the server still reports a wait (even one the clock has run past), the clock keeps ticking and re-reading.
    const cooling = entryIsWaiting(state);
    const waiting = cooling;
    if (cooling && state?.nextClaimAt) {
      const to = Date.parse(state.nextClaimAt);
      if (ticking === null) {
        const countdown = mountCountdown({ to, onElapsed: elapsed, now });
        ticking = { countdown, to };
        // One child: the button is a flex row, and a bare text node beside the clock would sit a gap apart.
        const label = document.createElement('span');
        label.append(document.createTextNode('Free tokens in '), countdown.root);
        root.replaceChildren(label);
      } else if (ticking.to !== to) {
        // The wait moved: the same label counts to the new time, nothing is rebuilt.
        ticking.to = to;
        ticking.countdown.setTarget(to);
      }
    } else {
      stopTicking();
      root.textContent = entryLabel(state, now());
    }
    root.classList.toggle('w21-warm', !waiting);
    root.dataset.state = waiting ? 'waiting' : state?.canClaim ? 'ready' : 'plain';
  };
  const off = subscribeFaucet(paint);

  return {
    root,
    setAccount(next, isGuest) {
      username = next;
      guest = isGuest;
      paint();
      // A guest has no state to show, but the answer can still be "off", and then there is no button to offer.
      if (next !== null) void loadFaucet(next).then(paint);
    },
    destroy() {
      off();
      stopTicking();
      root.remove();
    },
  };
}
